import { ROLES } from "../constants/roles";
import {
  QUERYPARAMS,
  QUERYPARAMSNAME,
} from "../layout/manualSwapLatest/common";
import paths from "../routes/paths";
import {
  ArrowLeftRight,
  Banknote,
  BatteryCharging,
  Calculator,
  House,
  UserPlus,
  ListPlus,
  Coins,
} from "lucide-react";

const BRAND = {
  primary: "#1DA7A7",
  dark: "#0F7A6D",
  light: "#E0F7F5",
  accent: "#00BFA5",
};

const ICONS = {
  home: House,
  cash: Banknote,
  swap: ArrowLeftRight,
  battery: BatteryCharging,
  calculator: Calculator,
  onboarding: ListPlus,
  lead: UserPlus,
  cashCollection: Coins,
};

const DEFAULT_BG = `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.accent} 100%)`;
const DEFAULT_BUTTON_BG = `linear-gradient(90deg, ${BRAND.primary} 0%, ${BRAND.dark} 100%)`;

export const MODULES = [
  {
    id: "home",
    label: "Home",
    path: paths.HOME,
    role: null,
    icon: ICONS.home,
    showInNav: true,
    showInDashboard: false,
  },

  {
    id: "cash-collection",
    label: "Cash Collection",
    path: paths.CASH_COLLECTION,
    role: ROLES.CASH_COLLECTION,
    icon: ICONS.cashCollection,
    description:
      "Track and manage cash pickups with real-time reports and automation.",
    buttonText: "Start Collection",
    statusDot: "#27AE60",
  },

  {
    id: "manual-swap",
    label: "Manual Swap",
    path: paths.MANUAL_SWAP,
    role: ROLES.MANUAL_SWAP,
    icon: ICONS.swap,
    description:
      "Perform quick battery swaps with guided steps and full visibility.",
    buttonText: "Initiate Swap",
    statusDot: "#27AE60",

    children: [
      {
        id: "manual-swap-overview",
        label: "Overview",
        path: paths.MANUAL_SWAP,
        role: ROLES.MANUAL_SWAP,
      },
      {
        id: "manual-swap-inventory",
        label: "Inventory",
        path: `${paths.MANUAL_SWAP}?${QUERYPARAMSNAME.SWAP_TYPE}=${QUERYPARAMS.INVENTORY}`,
        role: ROLES.MANUAL_INVENTORY,
      },
      {
        id: "manual-swap-digital-twin",
        label: "Digital Twin",
        path: `${paths.MANUAL_SWAP}?${QUERYPARAMSNAME.SWAP_TYPE}=${QUERYPARAMS.DIGITAL_TWIN}`,
        role: ROLES.MANUAL_DIGITAL,
      },
      {
        id: "manual-swap-ota",
        label: "OTA",
        path: `${paths.MANUAL_SWAP}?${QUERYPARAMSNAME.SWAP_TYPE}=${QUERYPARAMS.OTA}`,
        role: ROLES.MANUAL_OTA,
      },
    ],
  },

  {
    id: "battery-movement",
    label: "Battery Movement",
    path: paths.BATTERY_MOVEMENT,
    role: ROLES.BATTERY_MOVEMENT,
    icon: ICONS.battery,
    description: "Track battery transfers across states with live updates.",
    buttonText: "Track Movement",
    statusDot: "#F39C12",

    children: [
      {
        id: "battery-movement-overview",
        label: "Initiate",
        path: paths.BATTERY_MOVEMENT,
        role: ROLES.BATTERY_MOVEMENT,
      },
      {
        id: "battery-movement-logs",
        label: "Logs",
        path: `${paths.BATTERY_MOVEMENT}?view=logs`,
        role: ROLES.BATTERY_MOVEMENT,
      },
    ],
  },

  {
    id: "emi-calculator",
    label: "EMI Calculator",
    path: paths.EMI_CALCULATOR,
    role: ROLES.EMI_CALCULATOR,
    icon: ICONS.calculator,
    description: "Calculate battery loan EMI.",
    buttonText: "Start EMI Calculation",
    statusDot: "#27AE60",
  },

  {
    id: "lead-creation",
    label: "Lead Creation",
    path: paths.DRIVER_LEAD_CREATION,
    role: ROLES.DRIVER_LEAD_CREATION,
    icon: ICONS.lead,
    description: "Create driver lead.",
    buttonText: "Start Lead Creation",
    statusDot: "#27AE60",
  },

  {
    id: "onboarding",
    label: "Onboarding",
    path: paths.DRIVER_ONBOARDING,
    role: ROLES.ONBOARDING,
    icon: ICONS.onboarding,
    description:
      "Streamline driver onboarding with document collection and verification.",
    buttonText: "Start Onboarding",
    statusDot: "#27AE60",

    accessContent: {
      [ROLES.DRIVER_LEAD_VIEW]: {
        label: "Lead Management",
        description: "View driver leads and track onboarding progress.",
        buttonText: "View Leads",
        route: `${paths.DRIVER_ONBOARDING}?tab=leads`,
      },
      [ROLES.DEALER]: {
        label: "Lead Management",
        description:
          "View and manage driver leads and track onboarding progress.",
        buttonText: "Manage Leads",
        route: `${paths.DRIVER_ONBOARDING}?tab=leads`,
      },
      [ROLES.DRIVER_LEAD_CREATION]: {
        label: "Lead Management",
        description:
          "View and manage driver leads and track onboarding progress.",
        buttonText: "Manage Leads",
        route: `${paths.DRIVER_ONBOARDING}?tab=leads`,
      },
    },

    children: [
      {
        id: "leads",
        label: "Leads",
        path: `${paths.DRIVER_ONBOARDING}?tab=leads`,
        role: ROLES.DRIVER_LEAD_VIEW,
      },
      {
        id: "onboarding-pre",
        label: "Pre-onboarding",
        path: `${paths.DRIVER_ONBOARDING}?tab=pre-onboarding`,
        role: ROLES.ONBOARDING,
      },
      {
        id: "onboarded",
        label: "Onboarded",
        path: `${paths.DRIVER_ONBOARDING}?tab=onboarded`,
        role: ROLES.ONBOARDING,
      },
    ],
  },
];

export const getModuleBg = (m) => m.bgColor ?? DEFAULT_BG;
export const getModuleButtonBg = (m) => m.buttonColor ?? DEFAULT_BUTTON_BG;
